import Point from '@arcgis/core/geometry/Point';

import { Injectable } from '@angular/core';

import { EsriMap } from '@lib/geo/esri';
import { SnLonLat, SnLonLatLike } from '@lib/geo/helpers';

import { BaseGeometricNavigationService } from '../base-geometric-navigation.service';

@Injectable({
  providedIn: 'root',
})
export class EsriGeometricNavigationService extends BaseGeometricNavigationService {
  private _map?: EsriMap;

  constructor() {
    super();
  }

  public setMap(map: EsriMap): void {
    this._map = map;
  }

  public goToCoordinates(coords: SnLonLatLike, zoom: number = 18): void {
    if (!this._map) return;
    this.setCenter(coords);
    this._map.setZoom(zoom);
  }

  public async panTo(coords: SnLonLatLike, options?: Record<string, unknown>): Promise<void> {
    if (!this._map) return;
    const lonLat = SnLonLat.convert(coords);
    const target = {
      center: [lonLat.lon, lonLat.lat],
      ...options,
    };

    return this._map.panTo(target, options);
  }

  public getCenter(): SnLonLat {
    if (!this._map) {
      throw new Error('Map not set. Call setMap() first.');
    }
    
    if (this._map.mapView) {
      const center = this._map.mapView.center;
      return new SnLonLat(center.longitude, center.latitude);
    } else if (this._map.sceneView) {
      const center = this._map.sceneView.extent.center;
      return new SnLonLat(center.longitude, center.latitude);
    }
    throw new Error('No map view or scene view available');
  }

  public setCenter(coords: SnLonLatLike): void {
    if (!this._map?.mapView) {
      return;
    }

    const lonLat = SnLonLat.convert(coords);
    this._map.mapView.center = new Point({
      longitude: lonLat.lon,
      latitude: lonLat.lat,
    });
  }

  public async setCenterWithAnimation(lonLatLike: SnLonLatLike | number[]): Promise<void> {
    if (!this._map?.mapView) {
      return;
    }

    // Use a reasonable delay instead of checking private property
    await new Promise((resolve) => setTimeout(resolve, 100));

    let lon: number, lat: number;

    if (Array.isArray(lonLatLike)) {
      [lon, lat] = lonLatLike;
    } else {
      const lonLat = SnLonLat.convert(lonLatLike);
      lon = lonLat.lon;
      lat = lonLat.lat;
    }

    await this._map.panTo({
      center: [lon, lat],
    });
  }

  public setCenterSmart(
    lonLatLike: SnLonLatLike | number[],
    animated: boolean = true,
  ): Promise<void> | void {
    if (animated) {
      return this.setCenterWithAnimation(lonLatLike);
    } else {
      if (Array.isArray(lonLatLike)) {
        const [lon, lat] = lonLatLike;
        this.setCenter(new SnLonLat(lon, lat));
      } else {
        const lonLat = SnLonLat.convert(lonLatLike);
        this.setCenter(lonLat);
      }
    }
  }

  public setHeading(heading: number): void {
    if (this._map?.mapView) {
      this._map.mapView.rotation = heading;
    } else if (this._map?.sceneView) {
      this._map.sceneView.camera.heading = heading;
    }
  }
}
